import type { KeyboardEvent } from 'react';

import { cn } from '@/shared/utils/cn';

export type TabPillOption<T extends string = string> = {
  id: T;
  label: string;
};

export type TabPillSize = 'sm' | 'md';

export type TabPillsProps<T extends string> = {
  ariaLabel: string;
  size?: TabPillSize;
  /** Prefijo de los `id` que enlazan cada pill con su panel (`{idPrefix}-panel`). */
  idPrefix: string;
  tabs: readonly TabPillOption<T>[];
  activeId: T;
  onSelect: (id: T) => void;
  className?: string;
};

const SIZE_CLASSES: Record<TabPillSize, string> = {
  sm: 'px-3 py-1.5 text-xs',
  md: 'px-4 py-2 text-sm',
};

const FOCUS_KEYS = ['ArrowRight', 'ArrowLeft', 'Home', 'End'];

export const tabPillId = (idPrefix: string, tabId: string) => `${idPrefix}-tab-${tabId}`;

export const tabPanelId = (idPrefix: string) => `${idPrefix}-panel`;

export function TabPills<T extends string>({
  ariaLabel,
  size = 'md',
  idPrefix,
  tabs,
  activeId,
  onSelect,
  className,
}: TabPillsProps<T>) {
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!FOCUS_KEYS.includes(event.key)) return;
    const pills = Array.from(
      event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]'),
    );
    const current = pills.indexOf(event.target as HTMLButtonElement);
    if (current < 0) return;
    event.preventDefault();
    const last = pills.length - 1;
    const nextIndex: Record<string, number> = {
      ArrowRight: current === last ? 0 : current + 1,
      ArrowLeft: current === 0 ? last : current - 1,
      Home: 0,
      End: last,
    };
    pills[nextIndex[event.key]]?.focus();
  };

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={handleKeyDown}
      className={cn('flex flex-wrap gap-2', className)}
    >
      {tabs.map((tab) => {
        const active = tab.id === activeId;

        return (
          <button
            key={tab.id}
            id={tabPillId(idPrefix, tab.id)}
            type="button"
            role="tab"
            aria-selected={active}
            aria-controls={tabPanelId(idPrefix)}
            tabIndex={active ? 0 : -1}
            onClick={() => onSelect(tab.id)}
            className={cn(
              'duration-adipa-fast focus-visible:ring-adipa-focus-ring inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2',
              SIZE_CLASSES[size],
              active
                ? 'border-adipa-primary bg-adipa-primary text-adipa-primary-foreground'
                : 'border-adipa-border bg-adipa-surface text-adipa-text-secondary hover:border-adipa-primary/40 hover:text-adipa-text-primary',
            )}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}
